import {
  PriceAndSlider,
  SubscriptionCellDetails,
} from "../SubscriptionListModal";
import { useTranslation } from "next-i18next";
import {
  ExclamationCircleOutlinedDiv,
  HighlightDiv,
  StyledBlueBoldExplanationText,
  StyledBoldExplanationText,
  StyledCellButton,
  StyledCellDiv,
  StyledCellExplanationContainer,
  StyledCellExplanationLine,
  StyledCellSubtitle,
  StyledCellTitle,
  StyledCellWrapper,
  StyledCheckIcon,
  StyledComingUpDiv,
  StyledExplanationSubTitle,
  StyledNormalExplanationText,
  StyledUpgradeButton,
} from "../Style";
import { useEffect, useMemo, useState } from "react";
import { useGetPlansQuery } from "@/redux/api/paymentApi";
import DowngradeModal from "./DowngradeModal";
import TeamDowngradeModal from "./TeamDowngradeModal";
import { Tooltip } from "antd";
import styled from "styled-components";
import { PLAN_MATRIX } from "../constants";
import { getDashBoardState } from "@/redux/store";
import { useAppSelector } from "@/hooks/redux";
import { capitalize } from "@/lib/string-format-utils";
import { useGetSubscriptionsQuery, useGetUserInfoQuery } from "@/redux/api/userApi";

interface SubscriptionCellProps {
  period: boolean;
  plans: any;
  startSubscription: (plan: string, period: boolean, seats?: number) => void;
  upgradeSubscription: (plan: string, period: boolean, seats?: number) => void;
  downgradeSubscription: (plan: string, value: boolean, seats?: number) => void;
}

interface ExplanationLineProps {
  subTitle: string;
  frontText: string;
  boldText: string;
  backText: string;
  comingUp?: boolean;
  tooltip?: boolean;
  tooltipText?: string;
  subText?: string;
  blueBoldText?: string;
}

export interface PlanType {
  product: string;
  productDisplayName?: string;
  planName: string;
  planKey: string;
  constraints: {
    credit: number;
    automationCredit: number;
    seats?: number;
    totalTime?: number;
  };
  pricePerMonth: number;
  pricePerMonthByYearly: number;
  pricePerYear: number;
  currency: string;
  needContact?: boolean;
  isMostPopular?: boolean;
  iconPath?: string;
  descriptions?: string[];
  order: number;
  stripeProductId: string;
  planId: {
    monthly: string;
    yearly: string;
  };
  status: string;
  mark: string;
  subscriptionPermission?: string[];
}

const StyledTooltip = styled(Tooltip)``;

export const getPlanName = (name: string, interval: string) => {
  if (name === "none" || name === undefined) {
    return "free";
  }

  if (name?.includes("Personal") && interval === "year") {
    return "personal_yearly";
  }

  if (name?.includes("Personal") && interval !== "monthly") {
    return "personal_monthly";
  }

  if (name?.includes("Team") && interval === "year") {
    return "team_yearly";
  }

  if (name?.includes("Team") && interval !== "year") {
    return "team_monthly";
  }

  if (name?.includes("enter")) {
    return "enterprise";
  }

  return "free";
};

export const ExplanationLine = ({
  subTitle = "",
  frontText = "",
  boldText = "",
  backText = "",
  comingUp = false,
  tooltip = false,
  tooltipText = "",
  subText = "",
  blueBoldText = "",
}: ExplanationLineProps) => {
  const { t } = useTranslation("common");
  return (
    <StyledCellExplanationLine>
      {subTitle && (
        <StyledExplanationSubTitle>
          {t(`${subTitle}`)}
        </StyledExplanationSubTitle>
      )}
      <div className="main">
        {!subTitle && <StyledCheckIcon />}
        {frontText && (
          <StyledNormalExplanationText>
            {t(`${frontText}`)}
          </StyledNormalExplanationText>
        )}
        {boldText && (
          <StyledBoldExplanationText>
            {t(`${boldText}`)}
          </StyledBoldExplanationText>
        )}
        {backText && (
          <StyledNormalExplanationText>
            {t(`${backText}`)}
          </StyledNormalExplanationText>
        )}
        {blueBoldText && (
          <StyledBlueBoldExplanationText>
            {t(`${blueBoldText}`)}
          </StyledBlueBoldExplanationText>
        )}
        {comingUp && <StyledComingUpDiv>{t("Coming up")}</StyledComingUpDiv>}
        {tooltip && (
          <StyledTooltip title={t(tooltipText)}>
            <ExclamationCircleOutlinedDiv />
          </StyledTooltip>
        )}
      </div>
      {subText && (
        <StyledNormalExplanationText css={{ paddingLeft: "17px" }}>
          {t(`${subText}`)}
        </StyledNormalExplanationText>
      )}
    </StyledCellExplanationLine>
  );
};

export const PersonalCell = ({
  period,
  plans,
  startSubscription,
  upgradeSubscription,
  downgradeSubscription,
}: SubscriptionCellProps) => {
  const { t } = useTranslation("common");
  const [block, setBlock] = useState(false);
  const plan = "personal";
  const { data: accountUserInfo } = useGetUserInfoQuery(
    {},
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: true,
      skip: false,
    }
  );
  const [subscriptionType, setSubscriptionType] = useState("start");

  const handleUpdateSubscription = async () => {
    if (subscriptionType === "start") {
      await startSubscription(plan, period);
    } else if (subscriptionType === "upgrade") {
      await upgradeSubscription(plan, period);
    } else if (subscriptionType === "downgrade") {
      await downgradeSubscription(plan, true);
    }
  };

  useEffect(() => {
    const getButtonType = () => {
      const targetPlan = `${plan}_${period ? "monthly" : "yearly"}`;

      const myPlan = getPlanName(
        accountUserInfo?.plan,
        accountUserInfo?.interval
      );

      const targetPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === targetPlan
      );
      const myPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === myPlan
      );

      if (targetPlanIndex === -1 || myPlanIndex === -1) {
        return null;
      }

      return PLAN_MATRIX.matrix[myPlanIndex][targetPlanIndex];
    };

    const buttonType = getButtonType();

    if (buttonType) {
      setSubscriptionType(buttonType);
    } else {
    }
  }, [plan, period, accountUserInfo]);

  return (
    <StyledCellDiv style={{ marginTop: "32px" }}>
      <StyledCellTitle>{t("Personal")}</StyledCellTitle>
      <StyledCellSubtitle>
        {t("Powerful AI video creation for individual creators")}
      </StyledCellSubtitle>
      <PriceAndSlider
        period={period}
        plan={plan}
        plans={plans}
        seats={1}
        subscriptionType={subscriptionType}
      />
      {subscriptionType === "current" && (
        <div className="button-wrapper">
          <StyledUpgradeButton disabled>
            {t("Your current plan")}
          </StyledUpgradeButton>
        </div>
        // <div className="contact-text-sub">
        //   {t("Your personal plan is active!")}
        // </div>
      )}

      {subscriptionType === "block" && (
        <div className="button-wrapper">
          <div className="block"> &nbsp;</div>
        </div>
      )}

      {subscriptionType !== "block" &&
        subscriptionType !== "current" &&
        !accountUserInfo?.enterprise &&
        accountUserInfo?.company?.isMaster &&
        !accountUserInfo?.plan?.includes("Enterprise") && (
          <div className="button-wrapper">
            <StyledUpgradeButton
              type="primary"
              onClick={handleUpdateSubscription}
            >
              {subscriptionType === "start"
                ? t("Get Started")
                : subscriptionType === "upgrade"
                ? t("Upgrade")
                : t("Downgrade")}
            </StyledUpgradeButton>
          </div>
        )}
      <SubscriptionCellDetails plan={plan} plans={plans} period={period} />
      <DowngradeModal plan="personal" period={period} />
    </StyledCellDiv>
  );
};

export const TeamCell = ({
  period,
  plans,
  startSubscription,
  upgradeSubscription,
  downgradeSubscription,
}: SubscriptionCellProps) => {
  const { t } = useTranslation("common");
  const plan = "team";
  

  const { data: accountUserInfo } = useGetUserInfoQuery(
    {},
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: true,
      skip: false,
    }
  );

  const [subscriptionType, setSubscriptionType] = useState("start");
  const [seats, setSeats] = useState(1);

  const handleUpdateSubscription = async () => {
    if (subscriptionType === "start") {
      await startSubscription(plan, period, seats);
    } else if (subscriptionType === "upgrade") {
      await upgradeSubscription(plan, period, seats);
    } else if (subscriptionType === "downgrade") {
      await downgradeSubscription(plan, true, seats);
    }
  };

  useEffect(() => {
    const getButtonType = () => {
      const targetPlan = `${plan}_${period ? "monthly" : "yearly"}`;

      const myPlan = getPlanName(
        accountUserInfo?.plan,
        accountUserInfo?.interval
      );

      const targetPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === targetPlan
      );
      const myPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === myPlan
      );

      if (targetPlanIndex === -1 || myPlanIndex === -1) {
        return null;
      }

      return PLAN_MATRIX.matrix[myPlanIndex][targetPlanIndex];
    };

    const buttonType = getButtonType();

    if (buttonType) {
      setSubscriptionType(buttonType);
    } else {
    }
  }, [plan, period, accountUserInfo]);

  return (
    <StyledCellWrapper>
      <HighlightDiv>{t("Most Popular")}</HighlightDiv>
      <StyledCellDiv
        style={{
          borderTopRightRadius: "0px",
          borderTopLeftRadius: "0px",
          flex: 1,
        }}
      >
        <StyledCellTitle>{t("Team")}</StyledCellTitle>
        <StyledCellSubtitle>
          {t("Scalable AI video creation for growing teams")}
        </StyledCellSubtitle>
        <PriceAndSlider
          seats={seats}
          setSeats={setSeats}
          period={period}
          plan={plan}
          plans={plans}
          subscriptionType={subscriptionType}
        />

        {subscriptionType === "block" && (
          <div className="button-wrapper">
            <div className="block"> &nbsp;</div>
          </div>
        )}
        {subscriptionType === "current" && (
          // <div className="contact-text-sub">
          //   {t("Your team plan is active!")}
          // </div>
          <div className="button-wrapper">
            <StyledUpgradeButton onClick={handleUpdateSubscription} disabled>
              {t("Your current plan")}
            </StyledUpgradeButton>
          </div>
        )}

        {subscriptionType !== "current" &&
          subscriptionType !== "block" &&
          !accountUserInfo?.enterprise &&
          !accountUserInfo?.plan?.includes("Enterprise") &&
          accountUserInfo?.company?.isMaster && (
            <div className="button-wrapper">
              <StyledUpgradeButton onClick={handleUpdateSubscription}>
                {subscriptionType === "start"
                  ? t("Get Started")
                  : subscriptionType === "upgrade"
                  ? t("Upgrade")
                  : t("Downgrade")}
              </StyledUpgradeButton>
            </div>
          )}
        <SubscriptionCellDetails plan={plan} plans={plans} period={period} />
      </StyledCellDiv>
      <TeamDowngradeModal plan="team" period={period} />
    </StyledCellWrapper>
  );
};

export const EnterpriseCell = () => {
  const { t } = useTranslation("common");
  const { data: accountUserInfo } = useGetUserInfoQuery(
    {},
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: true,
      skip: false,
    }
  );

  const [subscriptionType, setSubscriptionType] = useState("start");
  const plan = "enterprise";

  const handleContact = (event: React.MouseEvent) => {
    if (window?.dataLayer?.push) {
      window.dataLayer.push({
        event: "generate_leads_enterprise_start",
        product_name: "AI Studios",
        location: "popup",
      });
    }

    event.preventDefault();
    const url = "https://www.deepbrain.io/company/contact";
    window.open(url);
  };

  useEffect(() => {
    const getButtonType = () => {
      const targetPlan = "enterprise";

      const myPlan = getPlanName(
        accountUserInfo?.plan,
        accountUserInfo?.interval
      );

      const targetPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === targetPlan
      );
      const myPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === myPlan
      );

      if (targetPlanIndex === -1 || myPlanIndex === -1) {
        return null;
      }

      return PLAN_MATRIX.matrix[myPlanIndex][targetPlanIndex];
    };

    const buttonType = getButtonType();

    if (buttonType) {
      setSubscriptionType(buttonType);
    } else {
    }
  }, [accountUserInfo?.interval, accountUserInfo?.plan]);

  return (
    <StyledCellDiv style={{ marginTop: "32px" }}>
      <StyledCellTitle>{t("Enterprise")}</StyledCellTitle>
      <StyledCellSubtitle>
        {t("Custom-built AI video solutions for enterprises")}
      </StyledCellSubtitle>
      <div className="enterpriseText">{t("Let's Talk")}</div>
      <div className="enterpriseSubText">{t("Custom Pricing")}</div>
      <div className="button-wrapper">
        <StyledCellButton
          type="default"
          onClick={handleContact}
          style={{ backgroundColor: "white", color: "black" }}
        >
          {t("Contact Us")}
        </StyledCellButton>
      </div>
      <SubscriptionCellDetails plan={"enterprise"} plans={[]} period={false} />
      {/* <StyledCellExplanationContainer>
        {enterprisePlanStrings.map((stringObj) => {
          return <ExplanationLine {...stringObj} key={stringObj.key} />;
        })}
      </StyledCellExplanationContainer> */}
    </StyledCellDiv>
  );
};

export const FreeCell = () => {
  const { t } = useTranslation("common");
  const { accountUserInfo } = useAppSelector((store) =>
    getDashBoardState(store)
  );
  const [subscriptionType, setSubscriptionType] = useState("start");
  const freePlanStrings = useMemo(() => {
    return [
      {
        key: "FreeTitle1",
        subTitle: t("What's included"),
        frontText: "",
        boldText: "",
        backText: "",
      },
      {
        key: "FreeText1",
        subTitle: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{exports}} Video Exports per Month", {
          exports: 3,
        }),
      },
      {
        key: "FreeText2",
        subTitle: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Videos Up to {{minutes}} mins", {
          minutes: 3
        }),
      },
      {
        key: "FreeText3",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{avatar}} Custom Avatar", {
          avatar: 1,
        }),
      },
      {
        key: "FreeText4",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{credits}} Generative Credits", {
          credits: 16,
        }),
      },
      {
        key: "FreeText5",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("{{resolution}} Video Export", {
          resolution: "720p",
        }),
      },
      {
        key: "FreeText6",
        subTitle: "",
        frontText: "",
        boldText: "",
        backText: "",
        blueBoldText: t("Standard Video Processing"),
      },

      {
        key: "FreeTitle2",
        subTitle: t("Key features"),
        frontText: "",
        boldText: "",
        backText: "",
        margin: true,
      },
      {
        key: "FreeText7",
        subTitle: "",
        frontText: t("AI Video Generation"),
        boldText: "",
        backText: "",
      },
      {
        key: "FreeText8",
        frontText: t("Video/Image Generator"),
      },
      {
        key: "FreeText9",
        subTitle: "",
        frontText: t("AI Dubbing"),
        boldText: "",
        backText: "",
      },
      {
        key: "FreeText10",
        frontText: t("Voice Cloning"),
      },
      {
        key: "FreeText11",
        frontText: t("{{avatars}} AI Generated Avatars", {
          avatars: "2,000+",
        }),
      },
      {
        key: "FreeText12",
        subTitle: "",
        frontText: t("Screen Recording with Avatar"),
        boldText: "",
        backText: "",
      },
      {
        key: "FreeText13",
        subTitle: "",
        frontText: t("{{languages}} Languages", {
          languages: "30+",
        }),
        boldText: "",
        backText: "",
      },
      {
        key: "FreeText14",
        subTitle: "",
        frontText: t("Share & Download Video"),
        boldText: "",
        backText: "",
      },
      {
        key: "FreeText15",
        subTitle: "",
        frontText: t("Watermark Included"),
        boldText: "",
        backText: "",
      },
    ];
  }, [t]);

  useEffect(() => {
    const getButtonType = () => {
      const targetPlan = "free";

      const myPlan = getPlanName(
        accountUserInfo?.plan,
        accountUserInfo?.interval
      );

      const targetPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === targetPlan
      );
      const myPlanIndex = PLAN_MATRIX.plans.findIndex(
        (item) => item === myPlan
      );

      if (targetPlanIndex === -1 || myPlanIndex === -1) {
        return null;
      }

      return PLAN_MATRIX.matrix[myPlanIndex][targetPlanIndex];
    };

    const buttonType = getButtonType();

    if (buttonType) {
      setSubscriptionType(buttonType);
    } else {
    }
  }, [accountUserInfo?.interval, accountUserInfo?.plan]);

  return (
    <StyledCellDiv style={{ marginTop: "32px" }}>
      <StyledCellTitle>{t("Free")}</StyledCellTitle>
      <StyledCellSubtitle>
        {t("Start your AI video journey completely free")}
      </StyledCellSubtitle>
      <PriceAndSlider
        period={false}
        plan="free"
        seats={1}
        setSeats={() => {}}
        subscriptionType={subscriptionType}
      />
      {/* <div className="button-wrapper">
        <StyledCellButton style={{ backgroundColor: "white", color: "black" }}>
          {t("Contact Us")}
        </StyledCellButton>
      </div> */}

      {subscriptionType === "current" && (
        <div className="button-wrapper">
          <StyledUpgradeButton disabled>
            {t("Your current plan")}
          </StyledUpgradeButton>
        </div>
      )}

      {subscriptionType !== "current" && (
        <div className="button-wrapper">
          <div className="block"> &nbsp;</div>
        </div>
      )}

      <StyledCellExplanationContainer>
        {freePlanStrings.map((stringObj) => {
          return <ExplanationLine {...stringObj} key={stringObj.key} />;
        })}
      </StyledCellExplanationContainer>
    </StyledCellDiv>
  );
};
